/**
 * Utility functions for parsing and processing markdown files
 */

// Simple function to parse markdown frontmatter
export const parseFrontmatter = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    console.warn('parseFrontmatter received non-string input:', markdown);
    return { frontmatter: {}, content: '' };
  }

  const frontmatterRegex = /^---\s*([\s\S]*?)\s*---/;
  const match = markdown.match(frontmatterRegex);
  
  if (!match) return { frontmatter: {}, content: markdown };
  
  const frontmatterText = match[1];
  const content = markdown.replace(frontmatterRegex, '').trim();
  
  // Parse frontmatter into an object
  const frontmatter = {};
  frontmatterText.split('\n').forEach(line => {
    const [key, ...valueArr] = line.split(':');
    if (key && valueArr.length) {
      let value = valueArr.join(':').trim();
      // Handle arrays in frontmatter (comma-separated values)
      if (value.includes(',')) {
        value = value.split(',').map(item => item.trim());
      }
      frontmatter[key.trim()] = value;
    }
  });
  
  return { frontmatter, content };
};

// Simple markdown to HTML conversion for common elements
export const markdownToHtml = (markdown) => {
  // Ensure markdown is a string
  if (!markdown || typeof markdown !== 'string') {
    console.warn('markdownToHtml received non-string input:', markdown);
    return '';
  }
  
  let html = markdown;
  
  // Convert headers
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');
  
  // Convert bold and italic
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert links
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Convert paragraphs (any line that's not a header)
  html = html.replace(/^(?!<h[1-6]>)(.*$)/gm, (match) => {
    if (match.trim() === '') return '';
    return `<p>${match}</p>`;
  });
  
  // Convert line breaks
  html = html.replace(/\n\s*\n/g, '<br/>');
  
  // Convert code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
  
  // Convert inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Convert unordered lists
  let inList = false;
  const listLines = html.split('\n').map(line => {
    if (line.trim().startsWith('- ')) {
      const content = line.trim().substring(2);
      if (!inList) {
        inList = true;
        return `<ul><li>${content}</li>`;
      }
      return `<li>${content}</li>`;
    } else if (inList) {
      inList = false;
      return `</ul>${line}`;
    }
    return line;
  });
  html = listLines.join('\n');
  if (inList) html += '</ul>';
  
  return html;
};

// Function to load blog posts
export const loadBlogPosts = async () => {
  try {
    // Get the list of blog posts from the index.json file
    const response = await fetch('/data/blog/index.json');
    if (!response.ok) {
      throw new Error('Failed to fetch blog index');
    }
    
    const blogIndex = await response.json();
    
    // Fetch each blog post content
    const posts = await Promise.all(
      blogIndex.map(async (post) => {
        try {
          const contentResponse = await fetch(`/data/blog/${post.slug}.md`);
          if (!contentResponse.ok) {
            throw new Error(`Failed to fetch blog post: ${post.slug}`);
          }
          
          const markdownText = await contentResponse.text();
          const { frontmatter, content } = parseFrontmatter(markdownText);
          
          // Merge the frontmatter with the post data from index
          return {
            ...post,
            title: frontmatter.title || post.title || 'Untitled Post',
            date: frontmatter.date || post.date || new Date().toISOString().split('T')[0],
            author: frontmatter.author || post.author || 'Admin',
            excerpt: frontmatter.excerpt || post.excerpt || 'No excerpt available',
            content: content,
            coverImage: frontmatter.coverImage || post.coverImage || 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1073&q=80',
            tags: frontmatter.tags || post.tags || [],
          };
        } catch (error) {
          console.error(`Error loading blog post: ${post.slug}`, error);
          return null;
        }
      })
    );
    
    // Filter out any null entries and sort by date (newest first)
    return posts.filter(Boolean).sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (error) {
    console.error('Error loading blog posts:', error);
    return [];
  }
};

// Function to get a blog post by slug
export const getBlogPostBySlug = async (slug) => {
  try {
    const contentResponse = await fetch(`/data/blog/${slug}.md`);
    
    if (!contentResponse.ok) {
      throw new Error(`Failed to fetch blog post with slug ${slug}`);
    }
    
    const markdownText = await contentResponse.text();
    const { frontmatter, content } = parseFrontmatter(markdownText);
    
    return {
      slug,
      title: frontmatter.title || 'Untitled Post',
      date: frontmatter.date || new Date().toISOString().split('T')[0],
      author: frontmatter.author || 'Admin',
      excerpt: frontmatter.excerpt || 'No excerpt available',
      content: content,
      coverImage: frontmatter.coverImage || 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1073&q=80',
      tags: frontmatter.tags || [],
    };
  } catch (error) {
    console.error(`Error loading blog post with slug ${slug}:`, error);
    return null;
  }
};